import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../services/api';
import { formatDistanceToNow } from 'date-fns';

interface Analytics {
  users: {
    total: number;
    admins: number;
    verified: number;
    active30d: number;
    active7d: number;
    new30d: number;
  };
  content: {
    babies: number;
    events: number;
  };
  support: {
    total: number;
    open: number;
  };
  growth: Array<{ date: string; count: number }>;
}

interface User {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  isAdmin: boolean;
  createdAt: string;
  lastActivityAt: string | null;
  babyCount: number;
  ticketCount: number;
  isLocked: boolean;
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="card p-5">
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { data: analytics, isLoading: loadingAnalytics } = useQuery({
    queryKey: ['admin-analytics'],
    queryFn: async () => {
      const res = await adminApi.getAnalytics();
      return res.data as Analytics;
    },
  });

  const { data: usersResult, isLoading: loadingUsers } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const res = await adminApi.listUsers({ limit: 20 });
      return res.data as User[];
    },
  });

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
        <a
          href="https://desk.zoho.eu"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary text-sm"
        >
          Open Zoho Desk ↗
        </a>
      </div>

      {/* Platform Stats */}
      {loadingAnalytics ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 animate-pulse">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="card p-5 h-24 bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>
      ) : analytics ? (
        <>
          <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Platform Overview
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Users" value={analytics.users.total} sub={`${analytics.users.verified} verified`} />
            <StatCard label="Total Babies" value={analytics.content.babies} />
            <StatCard label="Events Logged" value={analytics.content.events.toLocaleString()} />
            <StatCard label="Support Tickets" value={analytics.support.total} sub={`${analytics.support.open} open`} />
            <StatCard label="Active (7 days)" value={analytics.users.active7d} />
            <StatCard label="Active (30 days)" value={analytics.users.active30d} />
            <StatCard label="New Users (30d)" value={analytics.users.new30d} />
            <StatCard label="Admins" value={analytics.users.admins} />
          </div>

          {/* Daily registrations — simple sparkline-style table */}
          {analytics.growth && analytics.growth.length > 0 && (
            <div className="card p-5 mb-8">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Registrations (last 30 days)
              </h2>
              <div className="flex items-end gap-1 h-16">
                {analytics.growth.slice(-30).map((d, i) => {
                  const max = Math.max(...analytics.growth.map((x) => x.count), 1);
                  const pct = Math.round((d.count / max) * 100);
                  return (
                    <div
                      key={i}
                      className="flex-1 bg-primary-500 dark:bg-primary-400 rounded-sm min-h-[2px]"
                      style={{ height: `${Math.max(pct, 4)}%` }}
                      title={`${d.date}: ${d.count}`}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : null}

      {/* User List */}
      <div className="card">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Users</h2>
        </div>

        {loadingUsers ? (
          <div className="animate-pulse space-y-3 p-5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
        ) : usersResult && usersResult.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {usersResult.map((user) => (
              <div key={user.id} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white truncate">
                      {user.name}
                    </span>
                    {user.isAdmin && (
                      <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
                        Admin
                      </span>
                    )}
                    {!user.emailVerified && (
                      <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded">
                        Unverified
                      </span>
                    )}
                    {user.isLocked && (
                      <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded">
                        Locked
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {user.babyCount} {user.babyCount === 1 ? 'baby' : 'babies'} · {user.ticketCount} {user.ticketCount === 1 ? 'ticket' : 'tickets'}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    {user.lastActivityAt
                      ? `Active ${formatDistanceToNow(new Date(user.lastActivityAt), { addSuffix: true })}`
                      : `Joined ${formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="p-5 text-gray-500 dark:text-gray-400">No users found.</p>
        )}
      </div>
    </div>
  );
}
